import { Duration, intervalToDuration } from 'date-fns'
import { atom, useAtom } from 'jotai'
import { useCallback, useEffect, useRef } from 'react'
import { useUserState } from '~/atoms/UserState'
import { useQueueAlerts } from '~/hooks/useQueueToasts'
import {
  useGetAgentStatus,
  useGetUserAgentStatus,
  useQueueLogin,
  useQueueLogout,
  useUpdateUserAgentStatus,
} from '~/queries/queueQueries'
import Logger from '~/services/Logger'
import { Availability } from '~/types/AgentCustomStatus'

import { isAssignedAgentStatus } from '@mangovoice/cx-call-center-user-agents-client'
import { ApiError } from '@mangovoice/types'

const logger = new Logger('useCallQueue')

const STATUS_REFRESH_INTERVAL_MS = 1000 * 30

export const enum QueueAgentStatus {
  InQueue = 'IN_QUEUE',
  OutOfQueue = 'OUT_OF_QUEUE',
  DoNotDisturb = 'DO_NOT_DISTURB',
  Offline = 'OFFLINE',
}

export const isQueueAgentStatus = (value: unknown): value is QueueAgentStatus =>
  value === QueueAgentStatus.InQueue ||
  value === QueueAgentStatus.OutOfQueue ||
  value === QueueAgentStatus.DoNotDisturb ||
  value === QueueAgentStatus.Offline

export interface CustomQueueStatus {
  id: string
  label: string
  availability: Availability
  startTime: Date
}

const defaultStatus = QueueAgentStatus.OutOfQueue
const currentAgentStatusAtom = atom<QueueAgentStatus>(defaultStatus)
const currentCustomStatusAtom = atom<CustomQueueStatus | undefined>(undefined)
const elapsedTimeAtom = atom<Duration | undefined>(undefined)
const formattedTimeAtom = atom<string | undefined>(undefined)
const inQueueStartTimeAtom = atom<Date | undefined>(undefined)
const pendingStatusUpdateAtom = atom<boolean>(false)

// The status refresh is shared across every mounted consumer (usePhone, the
// queue button, the webphone page, ...). We keep the set of active consumers
// so a single global interval spans all of them and always ticks through a
// still-mounted consumer's refresh closure rather than one that has unmounted.
const queueStatusRefreshers: Array<() => void> = []
// When the last immediate fetch fired, across all mounts.
let lastInitialStatusFetchAt = 0

const startGlobalStatusRefresh = (refreshFn: () => void) => {
  queueStatusRefreshers.push(refreshFn)

  // Only the first active consumer starts the interval. Additional consumers
  // and rapid remounts reuse the running one instead of recreating it:
  // recreating reset the 30s countdown (which could starve the tick under
  // sustained churn) and each mount fired its own immediate request, stacking
  // load onto the throttle (support 00389200).
  if (globalThis.queueStatusInterval) {
    return
  }

  // Skip even this first immediate fetch if one fired within the interval
  // (e.g. all consumers just unmounted and remounted); the interval still
  // refreshes status within STATUS_REFRESH_INTERVAL_MS.
  const now = Date.now()
  if (now - lastInitialStatusFetchAt > STATUS_REFRESH_INTERVAL_MS) {
    lastInitialStatusFetchAt = now
    refreshFn()
  }

  globalThis.queueStatusInterval = setInterval(() => {
    // Count interval refreshes toward the de-dup window so a remount landing
    // just after a tick doesn't fire a redundant immediate fetch.
    lastInitialStatusFetchAt = Date.now()
    // Call the newest still-mounted consumer's closure.
    queueStatusRefreshers[queueStatusRefreshers.length - 1]?.()
  }, STATUS_REFRESH_INTERVAL_MS)
}

const stopGlobalStatusRefresh = (refreshFn: () => void) => {
  const index = queueStatusRefreshers.lastIndexOf(refreshFn)
  if (index !== -1) {
    queueStatusRefreshers.splice(index, 1)
  }

  // Tear the interval down only when the last consumer unmounts, so one of
  // several concurrent consumers leaving does not freeze refresh for the rest.
  if (queueStatusRefreshers.length === 0 && globalThis.queueStatusInterval) {
    clearInterval(globalThis.queueStatusInterval)
    globalThis.queueStatusInterval = undefined
  }
}

export const useCallQueue = () => {
  const [currentAgentStatus, setCurrentAgentStatus] = useAtom(
    currentAgentStatusAtom,
  )
  const [currentCustomStatus, setCurrentCustomStatus] = useAtom(
    currentCustomStatusAtom,
  )
  const [elapsedTime, setElapsedTime] = useAtom(elapsedTimeAtom)
  const [formattedTime, setFormattedTime] = useAtom(formattedTimeAtom)
  const [inQueueStartTime, setInQueueStartTime] = useAtom(inQueueStartTimeAtom)
  const [isPendingUpdate, setIsPendingUpdate] = useAtom(pendingStatusUpdateAtom)

  const { currentUserExtension } = useUserState()

  const queueLogin = useQueueLogin({ extensionId: currentUserExtension?.id })
  const queueLogout = useQueueLogout({ extensionId: currentUserExtension?.id })
  const updateUserAgentStatus = useUpdateUserAgentStatus({})

  const { showQueueUpdateErrorModal, showQueueUpdateGenericErrorModal } =
    useQueueAlerts()

  useEffect(() => {
    const startTime = currentCustomStatus?.startTime || inQueueStartTime
    if (
      !startTime ||
      (currentAgentStatus !== QueueAgentStatus.InQueue && !currentCustomStatus)
    )
      return

    const updateElapsedTime = () => {
      setElapsedTime(
        intervalToDuration({
          start: startTime,
          end: new Date(),
        }),
      )
    }

    updateElapsedTime()
    const interval = setInterval(updateElapsedTime, 1000)

    return () => clearInterval(interval)
  }, [currentCustomStatus, currentAgentStatus, inQueueStartTime])

  useEffect(() => {
    if (!elapsedTime) return

    setFormattedTime(
      elapsedTime.hours && elapsedTime.hours > 0
        ? `${String(elapsedTime.hours).padStart(2, '0')}:${String(
            elapsedTime.minutes ?? 0,
          ).padStart(
            2,
            '0',
          )}:${String(elapsedTime.seconds ?? 0).padStart(2, '0')}`
        : `${String(elapsedTime.minutes ?? 0).padStart(2, '0')}:${String(
            elapsedTime.seconds ?? 0,
          ).padStart(2, '0')}`,
    )
  }, [elapsedTime])

  const { refetch: refreshAgentStatus } = useGetAgentStatus({
    extensionId: currentUserExtension?.id,
  })

  const { refetch: refreshUserAgentStatus } = useGetUserAgentStatus({
    ext_uuid: currentUserExtension?.uuid,
  })

  const refreshStatusesRef = useRef<() => Promise<void>>(async () => {})

  // Fetches the authoritative queue status from the server and applies it to
  // local state. Unlike refreshStatuses, this does NOT skip while an update is
  // pending, so it can be used to reconcile after a partially-applied update.
  const reconcileFromServer = useCallback(async () => {
    if (!currentUserExtension?.uuid) {
      logger.warn('Skipping status refresh: missing extension UUID')
      return
    }

    const agentStatus = await refreshAgentStatus()
    const userAgentStatus = await refreshUserAgentStatus()

    if (!agentStatus || !userAgentStatus) return

    const assignedStatus = isAssignedAgentStatus(userAgentStatus.agent_status)
      ? userAgentStatus.agent_status
      : undefined

    if (agentStatus.status === 'Available') {
      logger.debug('Status changed to InQueue', {
        agentStatus,
        userAgentStatus,
      })
      setCurrentAgentStatus(QueueAgentStatus.InQueue)
      setCurrentCustomStatus(undefined)
      if (userAgentStatus.updated_at) {
        setInQueueStartTime(new Date(userAgentStatus.updated_at))
      } else {
        setInQueueStartTime(new Date())
      }
    } else if (agentStatus.status === 'Logged Out' && !assignedStatus) {
      // Only when CCUA truly has no assigned status after nil-UUID / null
      // guards (CUS-911). A real UUID with missing availability is a backend
      // bug (CUS-964) and must stay assigned in mapUserAgent, not land here.
      logger.debug('Status changed to OutOfQueue', {
        agentStatus,
        userAgentStatus,
      })
      setCurrentAgentStatus(QueueAgentStatus.OutOfQueue)
      setCurrentCustomStatus(undefined)
      setInQueueStartTime(undefined)
    } else if (assignedStatus) {
      logger.debug('Status changed to custom status', {
        agentStatus,
        userAgentStatus,
        customStatus: assignedStatus,
      })
      setCurrentAgentStatus(assignedStatus.id as unknown as QueueAgentStatus)
      setCurrentCustomStatus({
        id: assignedStatus.id,
        label: assignedStatus.name,
        availability: assignedStatus.availability,
        startTime: new Date(userAgentStatus.updated_at),
      })
    }
  }, [refreshAgentStatus, refreshUserAgentStatus, currentUserExtension?.uuid])

  const refreshStatuses = useCallback(async () => {
    if (isPendingUpdate) return
    await reconcileFromServer()
  }, [reconcileFromServer, isPendingUpdate])

  // Keep ref updated with latest function
  refreshStatusesRef.current = refreshStatuses

  useEffect(() => {
    // Wrapper that always calls the latest version
    const callLatestRefresh = () => {
      refreshStatusesRef.current?.()
    }

    startGlobalStatusRefresh(callLatestRefresh)

    return () => {
      stopGlobalStatusRefresh(callLatestRefresh)
    }
  }, [])

  const updateStatus = async (
    status: QueueAgentStatus,
    customStatus?: CustomQueueStatus,
  ) => {
    logger.debug('Updating status', { status, customStatus })
    const previousAgentStatus = currentAgentStatus
    const previousCustomStatus = currentCustomStatus
    setCurrentAgentStatus(status)
    setCurrentCustomStatus(customStatus)
    setIsPendingUpdate(true)

    try {
      switch (status) {
        case QueueAgentStatus.InQueue:
          await queueLogin()
          await updateUserAgentStatus(null, currentUserExtension?.uuid, true)
          setInQueueStartTime(new Date())
          logger.debug('Status update complete: InQueue')
          break
        case QueueAgentStatus.Offline:
          await queueLogout()
          await updateUserAgentStatus(null, currentUserExtension?.uuid, false)
          logger.debug('Status update complete: Offline')
          break
        default:
          await queueLogout()
          if (customStatus) {
            await updateUserAgentStatus(
              customStatus.id,
              currentUserExtension?.uuid,
              false,
            )
          }
          setInQueueStartTime(undefined)
          logger.debug('Status update complete: Custom', { customStatus })
          break
      }
    } catch (error) {
      // The queue login/logout/status endpoints can fail (e.g. a 404 when the
      // extension isn't in a call queue). Callers invoke updateStatus without
      // awaiting, so an escaping rejection surfaces as an unhandled promise
      // rejection in Sentry (WEB-APP-PRODUCTION-3M9). Surface the failure to
      // the user, and reconcile the optimistic UI with the server rather than
      // blindly reverting: the two mutations run sequentially, so on a partial
      // failure (first succeeds, second rejects) the server state no longer
      // matches previousAgentStatus. Fall back to the previous status only if
      // reconciliation itself fails.
      logger.warn('Failed to update queue status', { status, error })
      if ((error as ApiError)?.status === 404) {
        showQueueUpdateErrorModal()
      } else {
        showQueueUpdateGenericErrorModal()
      }
      try {
        await reconcileFromServer()
      } catch (reconcileError) {
        logger.warn('Failed to reconcile queue status after error', {
          reconcileError,
        })
        setCurrentAgentStatus(previousAgentStatus)
        setCurrentCustomStatus(previousCustomStatus)
      }
    } finally {
      setIsPendingUpdate(false)
    }
  }

  return {
    currentAgentStatus,
    currentCustomStatus,
    formattedTime,
    updateStatus,
  }
}

declare global {
  var queueStatusInterval: NodeJS.Timeout | undefined
}
